/**
 * Promotion overlay shown when a pawn reaches the last rank. chessground has
 * no built-in promotion UI, so we render this as a sibling of cg-wrap.
 *
 * Layout: a vertical strip 1 square wide × 4 squares tall, anchored to the
 * promotion-target column. The strip starts at the target square's edge and
 * extends toward the board center so Q is always closest to the target and N
 * is farthest.
 */
import React from 'react';
import {PIECE_IMAGES} from './pieces';

type PieceCode = 'q' | 'r' | 'b' | 'n';
const CHOICES: PieceCode[] = ['q', 'r', 'b', 'n'];

type Props = {
  color: 'w' | 'b';
  toSquare: string; // e.g. 'e8'
  orientation: 'white' | 'black';
  onSelect: (piece: PieceCode) => void;
  onCancel: () => void;
};

export default function Promotion({
  color,
  toSquare,
  orientation,
  onSelect,
  onCancel,
}: Props) {
  const fileIdx = toSquare.charCodeAt(0) - 'a'.charCodeAt(0); // 0..7
  const rankIdx = parseInt(toSquare[1], 10);                  // 1..8

  // Visual grid position (0,0 = top-left of the visible board).
  const visualCol = orientation === 'white' ? fileIdx : 7 - fileIdx;
  const visualRow = orientation === 'white' ? 8 - rankIdx : rankIdx - 1;

  // Strip extends into the board: from rows 0-3 it goes down; from rows 4-7
  // it goes up. Q is always closest to the promotion target.
  const extendsDownward = visualRow <= 3;
  const topPct = extendsDownward
    ? visualRow * 12.5
    : (visualRow - 3) * 12.5;
  const leftPct = visualCol * 12.5;

  return (
    <>
      {/* Backdrop: clicking outside cancels. zIndex 9 keeps it under chessground's
          drag layer but over the static board. */}
      <div
        onClick={onCancel}
        onContextMenu={e => {
          e.preventDefault();
          onCancel();
        }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.35)',
          zIndex: 9,
        }}
      />
      {/* The strip itself. */}
      <div
        style={{
          position: 'absolute',
          left: `${leftPct}%`,
          top: `${topPct}%`,
          width: '12.5%',
          height: '50%',
          zIndex: 10,
          display: 'flex',
          flexDirection: extendsDownward ? 'column' : 'column-reverse',
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          borderRadius: 4,
          overflow: 'hidden',
        }}>
        {CHOICES.map(p => {
          const key = color + p.toUpperCase();
          const img = PIECE_IMAGES[key];
          return (
            <button
              key={p}
              type="button"
              onClick={() => onSelect(p)}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <img
                src={img}
                alt={p}
                style={{width: '100%', height: '100%'}}
              />
            </button>
          );
        })}
      </div>
    </>
  );
}
